// Extract a human-readable message from an unknown throw. The shapes we see:
//   • `apiRequest` rejects with the axios `error.response` — i.e. the full AxiosResponse
//     (`{ status, data, headers, … }`) — so the backend reason is at `err.data.error.message`
//     (or `err.data.message`), NOT a top-level `err.message`. (The retry logic reads the
//     top-level `err.status` off this same response.)
//   • Stripe / other rejects: `{ error: { message } }` or a plain `{ message }`.
//   • A real `Error`: `err.message`.
// Returns undefined when none is found so callers fall back to clean copy rather than
// "[object Object]". Without the `err.data.error.message` branch a backend 400/503 would
// be logged as "Unknown error", defeating observability on the payment error paths.
//
// Shared by `CheckoutSessionsForm` (on-page confirm) and `CheckoutReturnClient` (the
// 3DS/redirect return path) so both surface the same backend reason from one implementation.
export const extractErrorMessage = (err: unknown): string | undefined => {
	if (typeof err === 'string') {
		return err;
	}
	if (err && typeof err === 'object') {
		const maybe = err as {
			message?: unknown;
			error?: { message?: unknown };
			data?: { message?: unknown; error?: { message?: unknown } };
		};
		if (typeof maybe.message === 'string') {
			return maybe.message;
		}
		if (typeof maybe.error?.message === 'string') {
			return maybe.error.message;
		}
		// AxiosResponse rejection (apiRequest): backend body under `data`.
		if (typeof maybe.data?.error?.message === 'string') {
			return maybe.data.error.message;
		}
		if (typeof maybe.data?.message === 'string') {
			return maybe.data.message;
		}
	}
	return undefined;
};

export const toError = (err: unknown): Error =>
	err instanceof Error ? err : new Error(extractErrorMessage(err) ?? 'Unknown error');
